import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { CheckCircle2, Award, TrendingUp, Users, Check, ArrowRight, Lightbulb, Globe, Target } from 'lucide-react';
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from 'react-router-dom';

const AboutUs = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  const [approachVisible, setApproachVisible] = useState(false);
  const [visionVisible, setVisionVisible] = useState(false);
  const approachRef = useRef<HTMLElement>(null);
  const visionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setHeroVisible(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target === approachRef.current) {
              setApproachVisible(true);
            } else if (entry.target === visionRef.current) {
              setVisionVisible(true);
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (approachRef.current) observer.observe(approachRef.current);
    if (visionRef.current) observer.observe(visionRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-32 pb-20 px-6 bg-secondary/5">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className={cn(
              "text-4xl md:text-5xl font-bold mb-6 transition-all duration-700",
              heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}
          >
            About Us
          </h1>
          <p 
            className={cn(
              "text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200",
              heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}
          >
            Discover our journey, mission, and the values that drive us to create exceptional software solutions.
          </p>
        </div>
      </section>
      
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-primary/10 rounded-lg text-sm font-medium mb-4">OUR STORY</span>
              <h2 className="text-3xl font-bold mb-6">A Passion for Innovation</h2>
              <p className="text-muted-foreground mb-6">
                Founded in 2025, Keplerion Tech began with a simple mission: to democratize access to cutting-edge technology. Our co-founders, with decades of combined experience in software engineering and artificial intelligence, noticed a gap in the market for solutions that were both powerful and accessible.
              </p>
              <p className="text-muted-foreground mb-6">
                What began as a focused effort to build meaningful and reliable software has steadily evolved into a studio dedicated to creating intelligent, intuitive digital products. We've stayed true to our original principles—craftsmanship, innovation, and a deep respect for the people who use our technology.
              </p>
              <p className="text-muted-foreground">
                Today, we continue to refine and expand our capabilities, developing solutions that simplify complexity and unlock new possibilities for businesses and individuals. As we look to the future, we remain committed to pushing the boundaries of what's possible in technology.
              </p>
            </div>
            <div className="relative">
              <div className="relative z-10 rounded-xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80" 
                  alt="A small engineering team working together on laptops around one table"
                  className="w-full h-auto aspect-[4/3] object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute top-10 -right-10 w-2/3 h-2/3 bg-primary/10 rounded-xl -z-10 transform rotate-6"></div>
              <div className="absolute -bottom-10 -left-10 w-2/3 h-2/3 bg-primary/5 rounded-xl -z-10 transform -rotate-6"></div>
            </div>
          </div>
        </div>
      </section>
      
      <section ref={approachRef} className="py-16 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMzODhCRkYiIGZpbGwtb3BhY2l0eT0iMC4wOCI+PHBhdGggZD0iTTM2IDE4YzEuMTUzIDAgMi4xLjg0NyAyLjI4IDEuOTVsMi40MjcuNDM2Yy0uNTUyLTUuMDk1LTQuODM3LTktMTAuMTItOUMyNS4wNTUgMTEuMzg2IDIwIDEyLjc2NyAyMCAxOC40djEuNmgxNnYtMnptLS4xNiA0Ljk1YzAgLjU1Mi0uNDQ4IDEtMSAxaC0xMHYtMmg1LjE2YzEuNTc1LjAyIDUuODQgMCAgICAgIDUuODQuMTk4di44MDJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20 z-0"></div>
        
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-blue-300/10 rounded-full blur-3xl z-0"></div>
        <div className="absolute top-1/2 -right-32 w-96 h-96 bg-indigo-300/10 rounded-full blur-3xl z-0"></div>
        <div className="absolute -bottom-40 left-1/3 w-72 h-72 bg-sky-300/10 rounded-full blur-3xl z-0"></div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-8 sm:mb-10">
            <span className={cn(
              "inline-block px-3 py-1 bg-primary/10 rounded-lg text-xs sm:text-sm font-medium mb-3 transition-all duration-700",
              approachVisible ? "opacity-100 animate-pulse" : "opacity-0"
            )}>OUR APPROACH</span>
            <h2 className={cn(
              "text-2xl sm:text-3xl font-bold mb-4 transition-all duration-700",
              approachVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}>How We Work With Clients</h2>
            <p className={cn(
              "text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200",
              approachVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}>
              Our client-centered methodology ensures we deliver solutions that truly address your business needs.
            </p>
          </div>
          
          <div className={cn(
            "relative mt-10 sm:mt-12",
            approachVisible ? "opacity-100" : "opacity-0"
          )}>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 lg:gap-12">
              <div className="w-full md:w-2/5 space-y-6">
                <div className={cn(
                  "bg-blue-50 rounded-2xl shadow-lg p-4 sm:p-5 transform transition-all duration-500 border border-blue-100/50",
                  approachVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10",
                  "hover:shadow-xl hover:-translate-y-2 relative overflow-hidden group"
                )}
                style={{ transitionDelay: "300ms" }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-bold text-sm">1</span>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-blue-600">Strategic Blueprint</h3>
                    </div>
                    <p className="text-slate-600">
                      We architect your digital vision through intensive discovery workshops, crafting a precision roadmap aligned with your unique business DNA.
                    </p>
                    <div className="w-0 h-0.5 bg-blue-200 mt-3 group-hover:w-full transition-all duration-500"></div>
                  </div>
                </div>
                
                <div className={cn(
                  "bg-sky-50 rounded-2xl shadow-lg p-4 sm:p-5 transform transition-all duration-500 border border-sky-100/50",
                  approachVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10",
                  "hover:shadow-xl hover:-translate-y-2 relative overflow-hidden group"
                )}
                style={{ transitionDelay: "500ms" }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-sky-50 to-sky-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
                        <span className="text-sky-600 font-bold text-sm">2</span>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-sky-600">Adaptive Evolution</h3>
                    </div>
                    <p className="text-slate-600">
                      Our dynamic sprint cycles pulse with innovation, continuously refining your solution through real-time feedback loops and cutting-edge techniques.
                    </p>
                    <div className="w-0 h-0.5 bg-sky-200 mt-3 group-hover:w-full transition-all duration-500"></div>
                  </div>
                </div>
              </div>
              
              <div className="relative my-4 md:my-0 mx-auto">
                <div className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 lg:w-60 lg:h-60 rounded-full bg-gradient-to-br from-blue-400/10 to-blue-600/10 flex items-center justify-center shadow-lg transform transition-all duration-500 hover:scale-105 group relative overflow-hidden">
                  <div className="absolute inset-0">
                    <div className="absolute w-2 h-2 rounded-full bg-blue-400/40 top-1/4 left-1/4 animate-[ping_3s_ease-in-out_infinite]"></div>
                    <div className="absolute w-2 h-2 rounded-full bg-sky-400/40 top-3/4 left-1/3 animate-[ping_2.5s_ease-in-out_0.5s_infinite]"></div>
                    <div className="absolute w-2 h-2 rounded-full bg-blue-400/40 bottom-1/4 right-1/4 animate-[ping_4s_ease-in-out_1s_infinite]"></div>
                    <div className="absolute w-2 h-2 rounded-full bg-sky-400/40 top-1/2 right-1/3 animate-[ping_3.5s_ease-in-out_0.7s_infinite]"></div>
                  </div>
                  
                  <div className="text-center p-3 sm:p-4 group-hover:scale-105 transition-all duration-300 z-10">
                    <h3 className="font-bold text-lg sm:text-xl md:text-2xl text-blue-700 group-hover:text-blue-800">Client Success</h3>
                    <p className="text-xs sm:text-sm md:text-base text-blue-600/80 mt-1 group-hover:text-blue-700/70">At the center of everything we do</p>
                    <div className="w-0 h-0.5 bg-blue-500/50 mx-auto mt-2 group-hover:w-3/4 transition-all duration-500"></div>
                  </div>
                </div>
                
                <div className="absolute inset-0 rounded-full border-2 border-dashed border-blue-300/30 transform scale-[1.2] animate-[spin_40s_linear_infinite] opacity-75"></div>
                <div className="absolute inset-0 rounded-full border-2 border-dashed border-blue-200/20 transform scale-[1.5] animate-[spin_60s_linear_infinite_reverse] opacity-60"></div>
                
                <div className="hidden md:block absolute top-1/2 -left-12 w-12 h-0.5 bg-gradient-to-r from-transparent to-blue-300/80 animate-[pulse_2s_ease-in-out_infinite]"></div>
                <div className="hidden md:block absolute top-1/2 -right-12 w-12 h-0.5 bg-gradient-to-l from-transparent to-blue-300/80 animate-[pulse_2s_ease-in-out_infinite]"></div>
              </div>
              
              <div className="w-full md:w-2/5 space-y-6">
                <div className={cn(
                  "bg-sky-50 rounded-2xl shadow-lg p-4 sm:p-5 transform transition-all duration-500 border border-sky-100/50",
                  approachVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10",
                  "hover:shadow-xl hover:-translate-y-2 relative overflow-hidden group"
                )}
                style={{ transitionDelay: "300ms" }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-sky-50 to-blue-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
                        <span className="text-sky-600 font-bold text-sm">3</span>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-sky-600">Elite Craftsmanship</h3>
                    </div>
                    <p className="text-slate-600">
                      We forge technological excellence through meticulous code reviews and comprehensive testing, delivering solutions with rock-solid reliability.
                    </p>
                    <div className="w-0 h-0.5 bg-sky-200 mt-3 group-hover:w-full transition-all duration-500"></div>
                  </div>
                </div>
                
                <div className={cn(
                  "bg-blue-50 rounded-2xl shadow-lg p-4 sm:p-5 transform transition-all duration-500 border border-blue-100/50",
                  approachVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10",
                  "hover:shadow-xl hover:-translate-y-2 relative overflow-hidden group"
                )}
                style={{ transitionDelay: "500ms" }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-sky-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-bold text-sm">4</span>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-blue-600">Growth Partnership</h3>
                    </div>
                    <p className="text-slate-600">
                      Beyond deployment, we provide ongoing strategic guidance and technical support, ensuring your digital assets evolve with your expanding business vision.
                    </p>
                    <div className="w-0 h-0.5 bg-blue-200 mt-3 group-hover:w-full transition-all duration-500"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Vision Section (hidden)
      <section ref={visionRef} className="py-20 px-6 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={cn(
              "text-3xl font-bold mb-6 transition-all duration-700",
              visionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}>Our Vision</h2>
            <p className={cn(
              "text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200",
              visionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}>
              We envision a world where businesses of all sizes have access to the transformative power of intelligent software solutions.
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-0 -left-10 w-40 h-40 bg-blue-300/10 rounded-full blur-3xl"></div>
              <div className="absolute top-10 right-10 w-60 h-60 bg-primary/5 rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 left-20 w-40 h-40 bg-yellow-300/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-10 right-20 w-60 h-60 bg-green-300/10 rounded-full blur-3xl"></div>
            </div>
            
            <div className={cn(
              "relative z-10 flex flex-col md:flex-row items-stretch gap-12 md:gap-0",
              visionVisible ? "opacity-100" : "opacity-0"
            )}>
              <div className="flex-1 relative">
                <div className={cn(
                  "relative transition-all duration-700 delay-300 md:pr-8 group",
                  visionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                )}>
                  <div className="hidden md:block absolute top-0 right-0 h-full w-px bg-primary/20"></div>
                  <div className="hidden md:flex absolute top-0 right-0 -mr-2.5 h-5 w-5 rounded-full bg-primary/10 items-center justify-center group-hover:bg-primary/30 transition-all duration-300">
                    <div className="h-2.5 w-2.5 rounded-full bg-primary"></div>
                  </div>
                  
                  <div className="flex md:block items-center gap-4">
                    <div className="flex h-14 w-14 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 shadow-md group-hover:shadow-lg transition-all duration-300 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-200/80 to-blue-300/40 opacity-70"></div>
                      <Lightbulb className="h-7 w-7 text-blue-700 group-hover:scale-110 transition-transform duration-300 z-10 m-auto" />
                    </div>
                    <div className="flex-1 md:mt-6">
                      <h3 className="text-xl font-semibold text-blue-700 mb-3">Innovation First</h3>
                      <p className="text-slate-600">
                        We're committed to staying at the forefront of technological advancement, constantly exploring new tools and methodologies to deliver cutting-edge solutions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 relative">
                <div className={cn(
                  "relative transition-all duration-700 delay-500 md:px-8 group",
                  visionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                )}>
                  <div className="hidden md:block absolute top-0 left-0 h-full w-px bg-primary/20"></div>
                  <div className="hidden md:block absolute top-0 right-0 h-full w-px bg-primary/20"></div>
                  <div className="hidden md:flex absolute top-0 left-0 -ml-2.5 h-5 w-5 rounded-full bg-primary/10 items-center justify-center group-hover:bg-primary/30 transition-all duration-300">
                    <div className="h-2.5 w-2.5 rounded-full bg-primary"></div>
                  </div>
                  
                  <div className="flex md:block items-center gap-4">
                    <div className="flex h-14 w-14 rounded-full bg-gradient-to-br from-blue-100 to-blue-300 shadow-md group-hover:shadow-lg transition-all duration-300 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-300/80 to-blue-400/40 opacity-70"></div>
                      <Globe className="h-7 w-7 text-blue-600 group-hover:scale-110 transition-transform duration-300 z-10 m-auto" />
                    </div>
                    <div className="flex-1 md:mt-6">
                      <h3 className="text-xl font-semibold text-blue-600 mb-3">Empowering Businesses</h3>
                      <p className="text-slate-600">
                        Our goal is to equip organizations with the technology they need to thrive in an increasingly digital world, regardless of their size or industry.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 relative">
                <div className={cn(
                  "relative transition-all duration-700 delay-700 md:pl-8 group",
                  visionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                )}>
                  <div className="hidden md:block absolute top-0 left-0 h-full w-px bg-primary/20"></div>
                  <div className="hidden md:flex absolute top-0 left-0 -ml-2.5 h-5 w-5 rounded-full bg-primary/10 items-center justify-center group-hover:bg-primary/30 transition-all duration-300">
                    <div className="h-2.5 w-2.5 rounded-full bg-primary"></div>
                  </div>
                  
                  <div className="flex md:block items-center gap-4">
                    <div className="flex h-14 w-14 rounded-full bg-gradient-to-br from-blue-100 to-blue-400 shadow-md group-hover:shadow-lg transition-all duration-300 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-400/80 to-blue-500/40 opacity-70"></div>
                      <Target className="h-7 w-7 text-blue-800 group-hover:scale-110 transition-transform duration-300 z-10 m-auto" />
                    </div>
                    <div className="flex-1 md:mt-6">
                      <h3 className="text-xl font-semibold text-blue-800 mb-3">Building Partnerships</h3>
                      <p className="text-slate-600">
                        We believe in creating lasting relationships with our clients, becoming a trusted technology partner rather than just a service provider.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}
      
      <section className="py-20 px-6 bg-slate-50 border-t border-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Work With Us?</h2>
          <p className="text-lg text-muted-foreground mb-10">
            Learn more about our team or reach out directly to start discussing your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/team" 
              className="px-8 py-3 rounded-full bg-secondary text-secondary-foreground font-medium hover:bg-secondary/80 transition-all"
            >
              Meet Our Team
            </Link>
            <Link 
              to="/contact" 
              className="px-8 py-3 rounded-full bg-primary text-white font-medium shadow-lg hover:shadow-xl hover:bg-primary/90 transition-all"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default AboutUs;
